// Navigation Manager - Orchestrates sidebar and menubar interactions
class NavigationManager {
    constructor() {
        this.currentView = 'trades';
        this.viewInstances = new Map();
        this.isTransitioning = false; // Guard flag to prevent concurrent transitions
        this.init();
    }

    /**
     * Initialize navigation manager
     */
    init() {
        this.setupSidebarIntegration();
        this.setupMenubarIntegration();
        // Don't load default view here - will be called after authentication
    }

    /**
     * Setup sidebar tab change handling
     */
    setupSidebarIntegration() {
        if (window.LeftSidebar) {
            window.LeftSidebar.onTabChanged((tabId) => {
                this.switchView(tabId);
            });
        }
    }

    /**
     * Setup menubar action handling
     */
    setupMenubarIntegration() {
        if (window.TopMenuBar && typeof window.TopMenuBar.onMenuActionTriggered === 'function') {
            window.TopMenuBar.onMenuActionTriggered((action) => {
                this.handleMenuAction(action);
            });
        }
    }

    /**
     * Switch to a different view with smooth transitions
     * @param {string} viewId - View identifier
     */
    async switchView(viewId) {
        if (this.currentView === viewId) {
            return;
        }

        // Prevent concurrent transitions
        if (this.isTransitioning) {
            // console.log(`[NAV] Ignoring rapid click - transition in progress`);
            return;
        }

        this.isTransitioning = true;

        // Show loading overlay
        if (window.Helpers) {
            window.Helpers.showLoading(true, '');
        }

        const previousView = this.currentView;
        const mainContent = document.getElementById('main-content');
        
        // Skip fade-out if startup overlay is present (prevents blink)
        const startupOverlay = document.getElementById('startup-dashboard-overlay');
        const shouldFade = !startupOverlay;
        
        // Add fade-out transition
        if (mainContent && shouldFade) {
            mainContent.style.opacity = '0';
            mainContent.style.transition = 'opacity 0.1s ease';
        }
        
        // Wait for fade-out to complete (only if fading)
        if (shouldFade) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }

        // Hide current view and wait for cleanup
        await this.hideCurrentView();

        // Show new view
        await this.showView(viewId);

        // Notify TradeDataManager about view change
        if (window.TradeDataManager) {
            try {
                window.TradeDataManager.onViewChange(previousView, viewId);
            } catch (error) {
                console.error('TradeDataManager view change notification error:', error);
            }
        }

        // Update sidebar active state
        if (window.LeftSidebar) {
            window.LeftSidebar.setActiveTab(viewId);
        }

        this.currentView = viewId;
        
        // Hide loading overlay
        if (window.Helpers) {
            window.Helpers.showLoading(false);
        }
        
        // Show sidebar after view loads (hidden by default in CSS)
        const leftSidebar = document.getElementById('left-sidebar');
        if (leftSidebar && leftSidebar.style.display !== 'flex') {
            leftSidebar.style.display = 'flex';
        }
        
        // Pre-warm BTC connections when NOT on terminal page
        if (viewId !== 'terminal' && previousView === 'terminal') {
            // Just left terminal - start pre-warming BTC in background
            this.prewarmBTC();
        }
        
        // Stop pre-warming when entering terminal (terminal will manage its own connections)
        if (viewId === 'terminal') {
            // Terminal view will handle its own connection management
        }
        
        // Reset transition flag
        this.isTransitioning = false;
    }

    /**
     * Hide current view
     */
    async hideCurrentView() {
        const currentViewElement = document.querySelector('.view-content.active');
        if (currentViewElement) {
            currentViewElement.classList.remove('active');
            currentViewElement.style.display = 'none';
        }

        // Cleanup current view if needed
        const currentViewInstance = this.viewInstances.get(this.currentView);
        if (currentViewInstance && currentViewInstance.onHide) {
            await currentViewInstance.onHide();
        }
    }

    /**
     * Show specific view
     * @param {string} viewId - View identifier
     */
    async showView(viewId) {
        // console.log('[NAV] ========== showView START ==========');
        // console.log('[NAV] viewId:', viewId);
        
        const viewContainer = document.getElementById('main-content');
        if (!viewContainer) {
            console.error('[NAV] ❌ Main content container not found');
            return;
        }
        // console.log('[NAV] main-content found, children:', viewContainer.children.length);

        // Get or create view element
        let viewElement = document.getElementById(`${viewId}-view`);
        // console.log('[NAV] Existing viewElement:', {
        //     found: !!viewElement,
        //     id: viewElement?.id,
        //     display: viewElement?.style?.display,
        //     innerHTML_length: viewElement?.innerHTML?.length
        // });
        
        if (!viewElement) {
            // console.log('[NAV] Creating new view element...');
            viewElement = await this.createViewElement(viewId);
            viewContainer.appendChild(viewElement);
            // console.log('[NAV] View element created and appended');
        }

        // Show view
        viewElement.classList.add('active');
        viewElement.style.display = 'block';
        // console.log('[NAV] View element shown:', {
        //     classList: viewElement.classList.toString(),
        //     display: viewElement.style.display
        // });

        // Initialize or show view instance
        let viewInstance = this.viewInstances.get(viewId);
        // console.log('[NAV] Cached viewInstance:', {
        //     found: !!viewInstance,
        //     instanceId: viewInstance?._instanceId
        // });
        
        if (!viewInstance) {
            // console.log('[NAV] Creating new view instance...');
            viewInstance = await this.createViewInstance(viewId);
            if (viewInstance) {
                this.viewInstances.set(viewId, viewInstance);
                // console.log('[NAV] New instance cached:', viewInstance._instanceId);
            }
        }

        if (viewInstance) {
            // Call onShow lifecycle method (views handle their own render logic)
            if (viewInstance.onShow) {
                try {
                    // console.log('[NAV] Calling onShow for instance:', viewInstance._instanceId);
                    await viewInstance.onShow();
                    // console.log('[NAV] ✅ onShow completed successfully');
                } catch (error) {
                    console.error(`[NAV] ❌ Error showing view ${viewId}:`, error);
                    // If onShow fails, try to recreate the view instance
                    // console.log('[NAV] Attempting to recreate view instance...');
                    this.viewInstances.delete(viewId);
                    const newViewInstance = await this.createViewInstance(viewId);
                    if (newViewInstance) {
                        this.viewInstances.set(viewId, newViewInstance);
                        // console.log('[NAV] New instance after retry:', newViewInstance._instanceId);
                        if (newViewInstance.onShow) {
                            try {
                                await newViewInstance.onShow();
                                // console.log('[NAV] ✅ Retry onShow completed');
                            } catch (retryError) {
                                console.error(`[NAV] ❌ Error showing view ${viewId} after retry:`, retryError);
                            }
                        }
                    }
                }
            }
        } else {
            console.error('[NAV] ❌ No viewInstance available for', viewId);
        }
        
        // console.log('[NAV] ========== showView END ==========');
        
        // Add fade-in transition after content is ready (only if not covered by overlay)
        const startupOverlay = document.getElementById('startup-dashboard-overlay');
        if (viewContainer && !startupOverlay) {
            // Small delay to ensure content is rendered
            await new Promise(resolve => setTimeout(resolve, 50));
            viewContainer.style.opacity = '1';
        } else if (viewContainer && startupOverlay) {
            // Set to full opacity immediately when overlay is present
            viewContainer.style.opacity = '1';
        }
    }

    /**
     * Create view DOM element
     * @param {string} viewId - View identifier
     * @returns {HTMLElement} - View element
     */
    async createViewElement(viewId) {
        const viewElement = document.createElement('div');
        viewElement.id = `${viewId}-view`;
        viewElement.className = 'view-content';
        
        // Don't create view instance here - let showView handle it to avoid duplicates
        viewElement.innerHTML = `<div class="view-loading">Loading ${viewId}...</div>`;
        
        return viewElement;
    }

    /**
     * Create view instance
     * @param {string} viewId - View identifier
     * @param {number} retryCount - Current retry attempt (internal)
     * @returns {Object} - View instance
     */
    async createViewInstance(viewId, retryCount = 0) {
        try {
            switch (viewId) {
                case 'dashboard':
                    const { DashboardView } = await import('../components/views/dashboard-view.js');
                    return new DashboardView();
                
                case 'terminal':
                    const { TerminalView } = await import('../components/views/terminal-view.js');
                    return new TerminalView();
                
                case 'trades':
                    // console.log('[NAV] 📥 Importing TradesView...');
                    const tradesModule = await import('../components/views/trades-view.js');
                    // console.log('[NAV] 📦 tradesModule:', tradesModule);
                    const { TradesView } = tradesModule;
                    // console.log('[NAV] 📦 TradesView class:', TradesView);
                    if (!TradesView) {
                        console.error('[NAV] ❌ TradesView is undefined after import!');
                        return null;
                    }
                    const tradesInstance = new TradesView();
                    // console.log('[NAV] ✅ TradesView instance created:', tradesInstance);
                    return tradesInstance;
                
                case 'charts':
                    const { AdvancedChartView } = await import('../components/charts/advanced-chart-view.js');
                    return new AdvancedChartView();
                
                case 'user-feed':
                    const { UserFeedView } = await import('../components/views/user-feed-view.js');
                    return new UserFeedView();
                
                case 'bots':
                    try {
                        const { BotsView } = await import('../components/views/bots-view.js');
                        return new BotsView();
                    } catch (importError) {
                        console.error('[NAV] ❌ Failed to import BotsView, using fallback:', importError);
                        // Return minimal fallback view that won't crash
                        return {
                            isInitialized: false,
                            container: null,
                            async onShow() {
                                this.container = document.getElementById('bots-view');
                                if (this.container) {
                                    this.container.innerHTML = `
                                        <div style="display: flex; align-items: center; justify-content: center; height: 100%; padding: 2rem;">
                                            <div style="text-align: center; max-width: 500px;">
                                                <h2 style="color: #ef4444; margin-bottom: 1rem;">⚠️ Bots View Unavailable</h2>
                                                <p style="color: #C8CBCE; margin-bottom: 1.5rem;">
                                                    The bots view failed to load. This may be due to a network issue or build problem.
                                                </p>
                                                <button onclick="location.reload()" style="
                                                    padding: 0.75rem 1.5rem;
                                                    background: #00BF63;
                                                    color: #0D0F11;
                                                    border: none;
                                                    border-radius: 6px;
                                                    font-weight: 600;
                                                    cursor: pointer;
                                                ">Reload Page</button>
                                            </div>
                                        </div>
                                    `;
                                }
                            },
                            async onHide() {},
                            render() {}
                        };
                    }
                
                case 'stablecoin-mm':
                    const { StableCoinMMView } = await import('../components/views/stablecoin-mm-view.js');
                    return new StableCoinMMView();
                
                case 'auto-trade':
                    const { AutoTradeView } = await import('../components/views/auto-trade-view.js');
                    const autoTradeView = new AutoTradeView();
                    window.autoTradeView = autoTradeView; // Set global reference for blacklist mode
                    return autoTradeView;
                
                case 'settings':
                    const { SettingsView } = await import('../components/views/settings-view.js');
                    return new SettingsView();
                
                case 'onboard':
                    const { OnboardView } = await import('../components/views/onboard-view.js');
                    return new OnboardView();
                
                case 'about':
                    const { AboutView } = await import('../components/views/about-view.js');
                    const aboutView = new AboutView();
                    window.aboutView = aboutView;
                    return aboutView;
            
                case 'performance':
                    const { PerformanceView } = await import('../components/views/performance-view.js');
                    return new PerformanceView();
            
                // Lighter Protocol Views
                case 'lighter-dashboard':
                    const { LighterDashboardView } = await import('../components/views/lighter/lighter-dashboard-view.js');
                    const lighterDashboard = new LighterDashboardView();
                    window.lighterDashboard = lighterDashboard;
                    return lighterDashboard;
            
                case 'lighter-terminal':
                    const { LighterTerminalView } = await import('../components/views/lighter/lighter-terminal-view.js');
                    const lighterTerminal = new LighterTerminalView();
                    window.lighterTerminal = lighterTerminal;
                    return lighterTerminal;
            
                case 'lighter-settings':
                    const { LighterSettingsView } = await import('../components/views/lighter/lighter-settings-view.js');
                    return new LighterSettingsView();
            
                case 'lighter-onboard':
                    const { LighterOnboardView } = await import('../components/views/lighter/lighter-onboard-view.js');
                    return new LighterOnboardView();
            
                case 'lighter-charts':
                    const { LighterChartsView } = await import('../components/views/lighter/lighter-charts-view.js');
                    return new LighterChartsView();
            
                case 'lighter-orders':
                    const { LighterOrdersView } = await import('../components/views/lighter/lighter-orders-view.js');
                    const lighterOrders = new LighterOrdersView();
                    window.lighterOrdersView = lighterOrders;
                    return lighterOrders;
            
                case 'lighter-performance':
                    const { LighterPerformanceView } = await import('../components/views/lighter/lighter-performance-view.js');
                    return new LighterPerformanceView();
            
                case 'lighter-signals':
                    // console.log('[NAV] 📥 Importing LighterSignalsView...');
                    const { LighterSignalsView } = await import('../components/views/lighter/lighter-signals-view.js');
                    // console.log('[NAV] ✅ LighterSignalsView imported, creating instance...');
                    const lighterSignalsInstance = new LighterSignalsView();
                    // console.log('[NAV] ✅ LighterSignalsView instance created:', lighterSignalsInstance._instanceId);
                    return lighterSignalsInstance;
            
                case 'lighter-bots':
                    const { LighterBotsView } = await import('../components/views/lighter/lighter-bots-view.js');
                    return new LighterBotsView();
            
                case 'lighter-stablecoin-mm':
                    const { LighterStableCoinMMView } = await import('../components/views/lighter/lighter-stablecoin-mm-view.js');
                    return new LighterStableCoinMMView();
            
                case 'lighter-auto-trade':
                    const { LighterAutoTradeView } = await import('../components/views/lighter/lighter-auto-trade-view.js');
                    return new LighterAutoTradeView();
            
                case 'lighter-about':
                    const { LighterAboutView } = await import('../components/views/lighter/lighter-about-view.js');
                    return new LighterAboutView();
            

                case 'alpha':
                    const { AlphaView } = await import('../components/views/alpha-view.js');
                    return new AlphaView();

                default:
                    return null;
            }
        } catch (error) {
            // Retry up to 3 times with 1 second delay
            if (retryCount < 3) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.createViewInstance(viewId, retryCount + 1);
            }
        
            // All retries exhausted - show error UI
            console.error(`Failed to load view ${viewId} after 3 attempts:`, error);
            
            // Update view element to show error message
            const viewElement = document.getElementById(`${viewId}-view`);
            if (viewElement) {
                viewElement.innerHTML = `
                    <div class="view-error" style="
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        padding: 2rem;
                        text-align: center;
                        color: #e0e0e0;
                    ">
                        <div style="
                            background: rgba(239, 68, 68, 0.1);
                            border: 1px solid rgba(239, 68, 68, 0.3);
                            border-radius: 8px;
                            padding: 2rem;
                            max-width: 500px;
                        ">
                            <h2 style="color: #EF4444; margin-bottom: 1rem;">Failed to Load View</h2>
                            <p style="margin-bottom: 1.5rem; line-height: 1.6;">
                                Could not load the <strong>${viewId}</strong> view after multiple attempts.
                                This may be due to a network issue or temporary server problem.
                            </p>
                            <p style="
                                background: rgba(0, 0, 0, 0.3);
                                padding: 1rem;
                                border-radius: 4px;
                                font-size: 0.9rem;
                                color: #a0a0a0;
                                margin-bottom: 1.5rem;
                            ">
                                Please restart the application to resolve this issue.
                            </p>
                        </div>
                    </div>
                `;
            }
            
            return null;
        }
    }

    /**
     * Handle menu bar actions
     * @param {string} action - Menu action
     */
    handleMenuAction(action) {
        switch (action) {
            case 'new-session':
                this.handleNewSession();
                break;
            case 'export-data':
                this.handleExportData();
                break;
            case 'refresh':
                this.handleRefresh();
                break;
            case 'fullscreen':
                this.handleFullscreen();
                break;
            case 'preferences':
                this.switchView('settings');
                break;
            case 'about':
                this.handleAbout();
                break;
            default:
                // console.log(`Menu action not implemented: ${action}`);
        }
    }

    /**
     * Load default view on startup
     */
    loadDefaultView() {
        // Start dashboard loading immediately (no artificial delay)
        this.switchView('dashboard').then(() => {
            // Remove overlay once dashboard is ready (instead of fixed 5 seconds)
            setTimeout(() => {
                this.hideStartupOverlay();
            }, 300); // Brief delay to ensure smooth transition
        });
    }

    /**
     * Show startup loading overlay
     */
    showStartupOverlay() {
        const mainContent = document.getElementById('main-content');
        
        if (mainContent) {
            // Create overlay that blocks visual output but allows processes
            const overlay = document.createElement('div');
            overlay.id = 'startup-dashboard-overlay';
            overlay.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: #1a1c1f;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 10000;
                backdrop-filter: blur(4px);
                opacity: 1;
                visibility: visible;
                overflow: hidden;
            `;
            
            overlay.innerHTML = `
                <!-- Grid Background -->
                <div style="
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image: 
                        linear-gradient(rgba(255, 255, 255, 0.08) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
                    background-size: 40px 40px;
                    background-position: center center;
                    mask-image: 
                        radial-gradient(ellipse 1200px 900px at center, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 25%, rgba(255, 255, 255, 0.6) 40%, rgba(255, 255, 255, 0.3) 60%, rgba(255, 255, 255, 0) 100%);
                    -webkit-mask-image: 
                        radial-gradient(ellipse 1200px 900px at center, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 25%, rgba(255, 255, 255, 0.6) 40%, rgba(255, 255, 255, 0.3) 60%, rgba(255, 255, 255, 0) 100%);
                    pointer-events: none;
                    opacity: 0.7;
                    z-index: 0;
                "></div>
                
                <div class="loading-container" style="
                    text-align: center;
                    color: #fff;
                    position: relative;
                    padding: 40px;
                    border-radius: 20px;
                    background: rgba(13, 15, 17, 0.9);
                    border: 1px solid rgba(255, 255, 253, 0.12);
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(20px);
                    opacity: 1;
                    transform: translateY(0) scale(1);
                    z-index: 1;
                ">
                    <!-- Logo/Brand Section -->
                    <div style="
                        margin-bottom: 10px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                    ">
                        <img src="../../icons/8.svg" 
                             style="
                                width: auto;
                                height: 64px;
                                object-fit: contain;
                                filter: drop-shadow(0 4px 12px rgba(0, 191, 99, 0.4));
                                animation: glow 2s ease-in-out infinite alternate;
                             " 
                             alt="ATv4">
                    </div>
                    
                    <!-- Main Loading Animation -->
                    <div class="loading-animation" style="
                        position: relative;
                        margin: 30px auto;
                        width: 80px;
                        height: 80px;
                    ">
                        <!-- Outer Ring -->
                        <div style="
                            position: absolute;
                            width: 80px;
                            height: 80px;
                            border: 2px solid rgba(0, 191, 99, 0.2);
                            border-top: 2px solid #00BF63;
                            border-radius: 50%;
                            animation: spin 2s linear infinite;
                        "></div>
                        
                        <!-- Middle Ring -->
                        <div style="
                            position: absolute;
                            top: 10px;
                            left: 10px;
                            width: 60px;
                            height: 60px;
                            border: 2px solid rgba(0, 191, 99, 0.15);
                            border-right: 2px solid #00BF63;
                            border-radius: 50%;
                            animation: spin 1.5s linear infinite reverse;
                        "></div>
                        
                        <!-- Inner Core -->
                        <div style="
                            position: absolute;
                            top: 20px;
                            left: 20px;
                            width: 40px;
                            height: 40px;
                            background: linear-gradient(45deg, #00BF63, #00BF63);
                            border-radius: 50%;
                            animation: pulse 1s ease-in-out infinite alternate;
                            box-shadow: 0 0 20px rgba(0, 191, 99, 0.5);
                        "></div>
                    </div>
                    
                    <!-- Loading Text -->
                    <div style="
                        font-size: 16px;
                        font-weight: 500;
                        margin-bottom: 15px;
                        animation: fadeInOut 2s ease-in-out infinite;
                    ">Initializing Dashboard...</div>
                    
                    <!-- Progress Dots -->
                    <div class="loading-dots" style="
                        display: flex;
                        justify-content: center;
                        gap: 8px;
                        margin-bottom: 20px;
                    ">
                        <div style="
                            width: 8px;
                            height: 8px;
                            background: #00BF63;
                            border-radius: 50%;
                            animation: dotPulse 1.4s ease-in-out infinite;
                            animation-delay: 0s;
                        "></div>
                        <div style="
                            width: 8px;
                            height: 8px;
                            background: #00BF63;
                            border-radius: 50%;
                            animation: dotPulse 1.4s ease-in-out infinite;
                            animation-delay: 0.2s;
                        "></div>
                        <div style="
                            width: 8px;
                            height: 8px;
                            background: #00BF63;
                            border-radius: 50%;
                            animation: dotPulse 1.4s ease-in-out infinite;
                            animation-delay: 0.4s;
                        "></div>
                    </div>
                    
                    <!-- Status Text -->
                    <div style="
                        font-size: 12px;
                        color: #C8CBCE;
                        opacity: 0.7;
                    ">Loading trading data and services...</div>
                </div>
                
                <style>
                    @keyframes smoothFadeIn {
                        from { 
                            opacity: 0;
                            backdrop-filter: blur(0px);
                        }
                        to { 
                            opacity: 1;
                            backdrop-filter: blur(10px);
                        }
                    }
                    
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    
                    @keyframes smoothFadeOut {
                        from { 
                            opacity: 1;
                            backdrop-filter: blur(10px);
                            transform: scale(1);
                        }
                        to { 
                            opacity: 0;
                            backdrop-filter: blur(0px);
                            transform: scale(0.98);
                        }
                    }
                    
                    @keyframes slideUp {
                        from { 
                            opacity: 0;
                            transform: translateY(30px) scale(0.95);
                        }
                        to { 
                            opacity: 1;
                            transform: translateY(0) scale(1);
                        }
                    }
                    
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    
                    @keyframes pulse {
                        from { 
                            transform: scale(1);
                            box-shadow: 0 0 20px rgba(116, 199, 236, 0.5);
                        }
                        to { 
                            transform: scale(1.1);
                            box-shadow: 0 0 30px rgba(116, 199, 236, 0.8);
                        }
                    }
                    
                    @keyframes glow {
                        from { 
                            text-shadow: 0 0 20px rgba(116, 199, 236, 0.5);
                        }
                        to { 
                            text-shadow: 0 0 30px rgba(116, 199, 236, 0.8), 0 0 40px rgba(137, 180, 250, 0.3);
                        }
                    }
                    
                    @keyframes fadeInOut {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.6; }
                    }
                    
                    @keyframes dotPulse {
                        0%, 80%, 100% { 
                            transform: scale(0.8);
                            opacity: 0.5;
                        }
                        40% { 
                            transform: scale(1.2);
                            opacity: 1;
                        }
                    }
                </style>
            `;
            
            document.body.appendChild(overlay);
        } else {
            console.error('[NAV] Cannot show startup overlay - main-content not found');
        }
    }

    /**
     * Hide startup loading overlay with smooth fade out
     */
    hideStartupOverlay() {
        const overlay = document.getElementById('startup-dashboard-overlay');
        if (overlay) {
            // Add fade out animation
            overlay.style.animation = 'smoothFadeOut 0.6s ease-out forwards';
            
            // Remove element after animation completes
            setTimeout(() => {
                if (overlay && overlay.parentNode) {
                    overlay.remove();
                }
            }, 600);
        }
    }

    /**
     * Handle new session
     */
    handleNewSession() {
        if (window.app && window.app.refreshData) {
            window.app.refreshData();
        }
    }

    /**
     * Handle export data
     */
    handleExportData() {
        if (window.app && window.app.exportData) {
            window.app.exportData();
        }
    }

    /**
     * Handle refresh
     */
    handleRefresh() {
        const currentViewInstance = this.viewInstances.get(this.currentView);
        if (currentViewInstance && currentViewInstance.refresh) {
            currentViewInstance.refresh();
        } else if (window.app && window.app.refreshData) {
            window.app.refreshData();
        }
    }

    /**
     * Handle fullscreen toggle
     */
    handleFullscreen() {
        if (window.electronAPI && window.electronAPI.window) {
            window.electronAPI.window.toggleFullscreen();
        }
    }

    /**
     * Handle about dialog
     */
    handleAbout() {
        // Show about modal or switch to about view
    }

    /**
     * Get current view
     * @returns {string} - Current view ID
     */
    getCurrentView() {
        return this.currentView;
    }

    /**
     * Update connection status across navigation components
     * @param {string} status - Connection status
     */
    updateConnectionStatus(status) {
        if (window.LeftSidebar) {
            window.LeftSidebar.updateConnectionStatus(status);
        }
    }

    /**
     * Pre-warm BTC connections when not on terminal page
     * Starts orderbook and candle trackers so they're ready when user returns to terminal
     */
    prewarmBTC() {
        const isElectron = window.process?.versions?.electron;
        
        if (!isElectron) {
            // Browser mode - pre-warm BTC connections in background
            if (window.BrowserOrderbookTracker) {
                window.BrowserOrderbookTracker.start('BTC').catch(err => {
                    // Silent fail - not critical
                });
            }
            
            if (window.BrowserCandleTracker) {
                window.BrowserCandleTracker.start('BTC', '15m').catch(err => {
                    // Silent fail - not critical
                });
            }
        }
        // Note: Electron mode doesn't need pre-warming (child processes are fast)
    }
}

// Export class only - instantiation handled by index.html
export { NavigationManager };
